import { marked, TokenizerAndRendererExtension } from 'marked'
import DOMPurify from 'isomorphic-dompurify'
import {
  isCinemaMessage,
  convertCinemaXmlToMarkdown,
  extractCinemaDisplaySegments,
  extractCinemaTtsText,
} from '@/lib/cinema'

const tts: TokenizerAndRendererExtension = {
  name: 'tts',
  level: 'inline',
  start(src) {
    return src.match(/["“”«»「」]/)?.index
  },
  tokenizer(src, tokens) {
    // 匹配引号["“”«»「」]包裹的内容，引号之间至少有一个字母或数字
    const reg =
      /^["“”«»「」]([^"“”«»「」\n]*[\p{L}\p{N}]+?[^"“”«»「」\n]*)["“”«»「」]/u
    const match = reg.exec(src)
    // 将引号及引号包裹的内容识别为 tts 类型 token
    if (match) {
      return {
        type: 'tts',
        raw: match[0],
        tokens: this.lexer.inlineTokens(match[1].trim()),
      }
    }
  },
  renderer(token) {
    // 将 tts 类型 token 渲染为带有高亮样式的 span
    return 'tokens' in token && token.tokens
      ? `<span class="markdown-tts-text">"${this.parser.parseInline(token.tokens)}"</span>`
      : token.text
  },
}
// 启用breaks选项，使得单个\n被转换为<br>标签而不是空格
marked.use({
  extensions: [tts],
  breaks: true,
})

const hasLetterOrNumber = (text: string): boolean => /[\p{L}\p{N}]/u.test(text)

// marked 渲染后的高亮台词形态固定为 span，分段 TTS 用它判断是否进入“只读引号台词”模式。
const createRenderedQuoteReg = () =>
  /<span class="markdown-tts-text">"(.+?)"<\/span>/g

// 星号动作在高亮台词里不朗读；普通文本模式会先剔除动作再判断是否还有可读内容。
const createRenderedEmReg = () => /<em>.*?<\/em>/g

// TTS 出口统一做可读性归一化，避免把标签、重复省略号或空白送进语音接口。
const normalizeReadableTtsText = (text: string): string => {
  const normalizedText = text
    .replace(/<.*?>|<\/.*?>/g, '')
    .replace(/…/g, '...')
    .replace(/\.{2,}/g, '.')
    .replace(/\s+/g, ' ')
    .trim()

  return hasLetterOrNumber(normalizedText) ? normalizedText : ''
}

// 图片不会在 TTS 中朗读，先移除 Markdown 图片语法，避免 alt 或 URL 进入语音文本。
const stripMarkdownImages = (text: string): string => {
  if (!text) return ''

  return text
    .replace(/!\[[^\]]*\]\([^()]*(?:\([^()]*\)[^()]*)*\)/g, '')
    .replace(/!\[[^\]]*\]/g, '')
}

// 普通文本进入 TTS 前的轻量清洗：去图片、代码块、行内代码和 tab 代码行。
const preparePlainTtsContent = (text: string): string => {
  let content = DOMPurify.sanitize(text, { ALLOWED_TAGS: [] })
  content = stripMarkdownImages(content).replace(/```(.*?)```/gs, '')
  content = content.replace(/`([^`]+)`/g, '')
  content = content.replace(/^\t.*$/gm, '')
  return content
}

// 判断可朗读语料之间是否真的存在可见灰字；纯标签、换行和空白不算分隔。
const hasVisibleNonSpeechContent = (html: string): boolean => {
  const visibleText = html
    .replace(/<[^>]*>/g, '')
    .replace(/&(?:nbsp|#160|#x0*a0);/gi, ' ')
    .replace(/&(?:#\d+|#x[\da-f]+|[a-z][a-z\d]+);/gi, '_')
    .replace(/\s+/g, '')

  return visibleText.length > 0
}

const appendTtsText = (
  currentText: string,
  nextText: string,
  separator: string
): string => (currentText ? `${currentText}${separator}${nextText}` : nextText)

// 引号台词模式下，仅灰字（引号外文本、em/code）会切开 texts；相邻白字仍留在同一项。
const extractQuotedCharacterSpeakTexts = (html: string): string[] => {
  const quoteMatches = Array.from(html.matchAll(createRenderedQuoteReg()))
  if (quoteMatches.length === 0) return []

  const texts: string[] = []
  let currentText = ''
  let previousQuoteEnd = 0
  let hasPendingNonSpeech = false

  quoteMatches.forEach((quoteMatch, quoteIndex) => {
    const quoteStart = quoteMatch.index ?? 0
    if (
      quoteIndex > 0 &&
      hasVisibleNonSpeechContent(html.slice(previousQuoteEnd, quoteStart))
    ) {
      hasPendingNonSpeech = true
    }

    const quoteContent = quoteMatch[1]
    const nonSpeechReg = /<(em|code)>[\s\S]*?<\/\1>/g
    let contentCursor = 0
    let isFirstTextInQuote = true

    for (const nonSpeechMatch of quoteContent.matchAll(nonSpeechReg)) {
      const nonSpeechStart = nonSpeechMatch.index ?? 0
      const speechText = normalizeReadableTtsText(
        quoteContent.slice(contentCursor, nonSpeechStart)
      )

      if (speechText) {
        if (hasPendingNonSpeech && currentText) {
          texts.push(currentText)
          currentText = speechText
        } else {
          currentText = appendTtsText(
            currentText,
            speechText,
            isFirstTextInQuote ? ' . ' : ' '
          )
        }
        hasPendingNonSpeech = false
        isFirstTextInQuote = false
      }

      if (hasVisibleNonSpeechContent(nonSpeechMatch[0])) {
        hasPendingNonSpeech = true
      }
      contentCursor = nonSpeechStart + nonSpeechMatch[0].length
    }

    const trailingSpeechText = normalizeReadableTtsText(
      quoteContent.slice(contentCursor)
    )
    if (trailingSpeechText) {
      if (hasPendingNonSpeech && currentText) {
        texts.push(currentText)
        currentText = trailingSpeechText
      } else {
        currentText = appendTtsText(
          currentText,
          trailingSpeechText,
          isFirstTextInQuote ? ' . ' : ' '
        )
      }
      hasPendingNonSpeech = false
    }

    previousQuoteEnd = quoteStart + quoteMatch[0].length
  })

  if (currentText) texts.push(currentText)
  return texts.length > 1 ? texts : []
}

// 展示 HTML 已经过净化；复用它提取 TTS 时，只需移除图片和代码内容，避免再次跑 marked/DOMPurify。
const stripRenderedTtsMedia = (html: string): string =>
  html
    .replace(/<code\b[^>]*>[\s\S]*?<\/code>/gi, '')
    .replace(/<img\b[^>]*>/gi, '')

// 无引号模式下，普通文字都可朗读，仅 Markdown 强调语法渲染出的灰色 em 会切开 texts。
const extractPlainCharacterSpeakTextsFromHtml = (
  renderedHtml: string
): string[] => {
  const html = stripRenderedTtsMedia(renderedHtml)
  const nonSpeechReg = /<em>[\s\S]*?<\/em>/g
  const texts: string[] = []
  let currentText = ''
  let contentCursor = 0
  let hasPendingNonSpeech = false

  for (const nonSpeechMatch of html.matchAll(nonSpeechReg)) {
    const nonSpeechStart = nonSpeechMatch.index ?? 0
    const speechText = normalizeReadableTtsText(
      html.slice(contentCursor, nonSpeechStart)
    )

    if (speechText) {
      if (hasPendingNonSpeech && currentText) {
        texts.push(currentText)
        currentText = speechText
      } else {
        currentText = appendTtsText(currentText, speechText, ' ')
      }
      hasPendingNonSpeech = false
    }

    if (hasVisibleNonSpeechContent(nonSpeechMatch[0])) {
      hasPendingNonSpeech = true
    }
    contentCursor = nonSpeechStart + nonSpeechMatch[0].length
  }

  const trailingSpeechText = normalizeReadableTtsText(html.slice(contentCursor))
  if (trailingSpeechText) {
    if (hasPendingNonSpeech && currentText) {
      texts.push(currentText)
      currentText = trailingSpeechText
    } else {
      currentText = appendTtsText(currentText, trailingSpeechText, ' ')
    }
  }

  if (currentText) texts.push(currentText)
  return texts.length > 1 ? texts : []
}

const extractCharacterSpeakTextsFromHtml = (renderedHtml: string): string[] => {
  if (createRenderedQuoteReg().test(renderedHtml)) {
    return extractQuotedCharacterSpeakTexts(renderedHtml)
  }

  return extractPlainCharacterSpeakTextsFromHtml(renderedHtml)
}

// 没有高亮台词时，普通文本也允许朗读，但星号动作仍不送入 TTS。
const extractPlainTtsText = (text: string): string => {
  if (!text.trim()) return ''
  if (isCinemaMessage(text)) return extractTtsText(text)

  const plainText = preparePlainTtsContent(text).replace(/\*(.*?)\*/gs, '')
  return normalizeReadableTtsText(plainText)
}

// 严格模式只提取明确标记的引号台词，不对局部纯文本做整段朗读兜底。
const extractExplicitTtsTextFromHtml = (html: string): string => {
  const codeFreeHtml = html.replace(/<code>.*?<\/code>/gs, '')
  const matches = Array.from(
    codeFreeHtml.matchAll(createRenderedQuoteReg()),
    (match) => match[1]
  )
  const explicitTtsText = matches
    .map((match) => match.replace(createRenderedEmReg(), ''))
    .join(' . ')

  return normalizeReadableTtsText(explicitTtsText)
}

const extractTtsTextFromHtml = (html: string): string => {
  // 保持历史 TTS 口径：带 language class 的 fenced code 仍进入整条 fallback。
  const codeFreeHtml = html.replace(/<code>.*?<\/code>/gs, '')
  const renderedQuoteReg = createRenderedQuoteReg()
  const renderedEmReg = createRenderedEmReg()

  if (
    codeFreeHtml.match(renderedQuoteReg) ||
    codeFreeHtml.match(renderedEmReg)
  ) {
    return extractExplicitTtsTextFromHtml(codeFreeHtml)
  }

  return normalizeReadableTtsText(codeFreeHtml)
}

// 先在整条消息上确认是否存在真实语音语料，再决定分段后能否启用纯文本兜底。
const extractExplicitTtsText = (text: string): string => {
  if (!text.trim() || isCinemaMessage(text)) return ''
  return extractExplicitTtsTextFromHtml(transformToMarkdownHtml(text))
}

// 智能处理引号前的换行，确保 <br> 不会被块级元素吸收
// 如果引号前面有换行（中间可以有空格），就再补充一个换行
export function ensureQuoteParagraphSeparation(text: string): string {
  return text.replace(
    /(\n[\s]*?)(["“”«»「」][^"“”«»「」\n]*[\p{L}\p{N}]+?[^"“”«»「」\n]*["“”«»「」])/gu,
    (match, beforeQuote, quote) => {
      // 计算引号前的换行数量
      const newlineCount = (beforeQuote.match(/\n/g) || []).length

      if (newlineCount === 1) {
        // 只有一个换行，需要再补充一个，形成双换行
        return '\n\n' + quote
      }

      // 如果已经有双换行或以上，保持不变
      return match
    }
  )
}

// 从角色消息中提取对话文本
export function extractTtsText(text: string): string {
  // 检查是否是 cinema 模式消息
  if (isCinemaMessage(text)) {
    // Cinema 模式：提取 <dialog> 标签中的对话内容
    const cinemaTts = extractCinemaTtsText(text)
    if (cinemaTts) {
      const normalizedCinemaTts = cinemaTts
        .replace(/…/g, '...') // 将省略号替换为三个点
        .replace(/\.{2,}/g, '.') // 将多个点替换为一个点，适配语音模型
        .replace(/\s+/g, ' ') // 将多个空格替换为一个空格，适配语音模型
        .trim()
      return hasLetterOrNumber(normalizedCinemaTts) ? normalizedCinemaTts : ''
    }
    return ''
  }

  return extractTtsTextFromHtml(transformToMarkdownHtml(text))
}

// 仅接收一个外层语音分段：保留 text 现有合并结果，把段内灰字隔开的语料传给后端。
export function extractCharacterSpeakTexts(text: string): string[] {
  if (!text.trim()) return []

  if (isCinemaMessage(text)) {
    // Cinema 的每个 <dialog> 已是独立外层语音分段，不能再跨 segment 组装 texts。
    return []
  }

  return extractCharacterSpeakTextsFromHtml(transformToMarkdownHtml(text))
}

// 返回紧凑的可朗读段落列表，仅用于判断是否要展示多个语音 chip。
export function extractTtsSegments(text: string): string[] {
  if (!text.trim()) return []

  if (isCinemaMessage(text)) {
    const cinemaSegments = extractCinemaDisplaySegments(text)
      .map((segment) => normalizeReadableTtsText(segment.ttsText))
      .filter((segment) => segment.length > 0)

    if (cinemaSegments.length > 0) return cinemaSegments

    const fallback = extractTtsText(text)
    return fallback ? [fallback] : []
  }

  const explicitTtsText = extractExplicitTtsText(text)
  if (!explicitTtsText) {
    return text
      .split(/\n(?:[ \t]*\n)+/)
      .map((segment) => extractPlainTtsText(segment))
      .filter((segment) => segment.length > 0)
  }

  const segments = text
    .split(/\n(?:[ \t]*\n)+/)
    .map((segment) => extractExplicitTtsText(segment))
    .filter((segment) => segment.length > 0)

  if (segments.length > 0) return segments
  return [explicitTtsText]
}

export type DisplayTtsSegment = {
  // 保留原始段落用于气泡展示，markdown/动作描写都不丢。
  displayText: string
  // 清洗后的文本用于请求 TTS；空字符串表示该段只展示不朗读。
  ttsText: string
  // 同一语音分段内被灰字隔开的语料；text 仍使用上面的 ttsText，不做改变。
  ttsTexts: string[]
  // 只有可朗读段落占用后端 segment_index；纯动作段为 null。
  voiceSegmentIndex: number | null
}

export type RenderedDisplayTtsSegment = DisplayTtsSegment & {
  // 与当前段对应的净化 HTML，展示层直接复用，不再重复解析 Markdown。
  html: string
}

export type ChatMessagePresentation = {
  html: string
  displayTtsSegments: RenderedDisplayTtsSegment[]
  ttsSegmentRequests: Array<{ text: string; texts: string[] }>
  ttsSegments: string[]
  wholeMessageTtsTexts: string[]
  ttsContent: string
}

type ChatMessagePresentationOptions = {
  includeTts?: boolean
}

const buildRenderedDisplayTtsSegments = (
  text: string,
  renderedHtml: string
): RenderedDisplayTtsSegment[] => {
  if (!text.trim()) return []

  if (isCinemaMessage(text)) {
    const cinemaSegments = extractCinemaDisplaySegments(text)
    if (cinemaSegments.length > 0) {
      let voiceSegmentIndex = 0
      return cinemaSegments.map((segment) => {
        const ttsText = normalizeReadableTtsText(segment.ttsText)
        const html =
          cinemaSegments.length === 1
            ? renderedHtml
            : transformToMarkdownHtml(segment.displayText)
        return {
          displayText: segment.displayText,
          html,
          ttsText,
          ttsTexts: ttsText ? extractCharacterSpeakTextsFromHtml(html) : [],
          voiceSegmentIndex: ttsText ? voiceSegmentIndex++ : null,
        }
      })
    }

    const fallback = extractTtsText(text)
    return fallback
      ? [
          {
            displayText: convertCinemaXmlToMarkdown(text),
            html: renderedHtml,
            ttsText: fallback,
            ttsTexts: [],
            voiceSegmentIndex: 0,
          },
        ]
      : []
  }

  const rawSegments = text
    .split(/\n(?:[ \t]*\n)+/)
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0)

  const explicitTtsText = extractExplicitTtsTextFromHtml(renderedHtml)
  if (!explicitTtsText) {
    let voiceSegmentIndex = 0
    return rawSegments.map((displayText) => {
      const html =
        rawSegments.length === 1
          ? renderedHtml
          : transformToMarkdownHtml(displayText)
      const ttsText = extractPlainTtsText(displayText)
      return {
        displayText,
        html,
        ttsText,
        ttsTexts: ttsText ? extractCharacterSpeakTextsFromHtml(html) : [],
        voiceSegmentIndex: ttsText ? voiceSegmentIndex++ : null,
      }
    })
  }

  let voiceSegmentIndex = 0
  const displaySegments = rawSegments.map((displayText) => {
    const html =
      rawSegments.length === 1
        ? renderedHtml
        : transformToMarkdownHtml(displayText)
    const ttsText = extractExplicitTtsTextFromHtml(html)
    return {
      displayText,
      html,
      ttsText,
      ttsTexts: ttsText ? extractCharacterSpeakTextsFromHtml(html) : [],
      voiceSegmentIndex: ttsText ? voiceSegmentIndex++ : null,
    }
  })

  if (voiceSegmentIndex > 0 || displaySegments.length > 0) {
    return displaySegments
  }

  return explicitTtsText
    ? [
        {
          displayText: text.trim(),
          html: renderedHtml,
          ttsText: explicitTtsText,
          ttsTexts: [],
          voiceSegmentIndex: 0,
        },
      ]
    : []
}

// 同时产出展示段和 TTS 段，确保 UI 分段边界与后端 segment_index 对齐。
export function extractDisplayTtsSegments(text: string): DisplayTtsSegment[] {
  if (!text.trim()) return []

  // 共享入口不需要展示 HTML；Cinema 只解析可朗读 dialog，避免渲染整条消息和纯展示段。
  if (isCinemaMessage(text)) {
    const cinemaSegments = extractCinemaDisplaySegments(text)
    if (cinemaSegments.length > 0) {
      let voiceSegmentIndex = 0
      return cinemaSegments.map((segment) => {
        const ttsText = normalizeReadableTtsText(segment.ttsText)
        return {
          displayText: segment.displayText,
          ttsText,
          ttsTexts: ttsText
            ? extractCharacterSpeakTexts(segment.displayText)
            : [],
          voiceSegmentIndex: ttsText ? voiceSegmentIndex++ : null,
        }
      })
    }

    const fallback = extractTtsText(text)
    return fallback
      ? [
          {
            displayText: convertCinemaXmlToMarkdown(text),
            ttsText: fallback,
            ttsTexts: [],
            voiceSegmentIndex: 0,
          },
        ]
      : []
  }

  return buildRenderedDisplayTtsSegments(
    text,
    transformToMarkdownHtml(text)
  ).map((segment) => ({
    displayText: segment.displayText,
    ttsText: segment.ttsText,
    ttsTexts: segment.ttsTexts,
    voiceSegmentIndex: segment.voiceSegmentIndex,
  }))
}

// 普通聊天气泡的一次性派生模型：HTML、TTS 和分段元数据共享同一批解析结果。
export function buildChatMessagePresentation(
  text: string,
  options: ChatMessagePresentationOptions = {}
): ChatMessagePresentation {
  const html = transformToMarkdownHtml(text)
  if (options.includeTts === false) {
    return {
      html,
      displayTtsSegments: [],
      ttsSegmentRequests: [],
      ttsSegments: [],
      wholeMessageTtsTexts: [],
      ttsContent: '',
    }
  }

  const displayTtsSegments = buildRenderedDisplayTtsSegments(text, html)
  const ttsSegmentRequests = displayTtsSegments
    .filter((segment) => segment.ttsText.length > 0)
    .map((segment) => ({
      text: segment.ttsText,
      texts: segment.ttsTexts,
    }))
  const ttsSegments = ttsSegmentRequests.map((segment) => segment.text)
  const wholeMessageTtsTexts =
    ttsSegmentRequests.length === 1 ? ttsSegmentRequests[0].texts : []
  const ttsContent = (
    ttsSegments.length > 0
      ? ttsSegments.join(' ')
      : isCinemaMessage(text)
        ? extractTtsText(text)
        : extractTtsTextFromHtml(html)
  ).trim()

  return {
    html,
    displayTtsSegments,
    ttsSegmentRequests,
    ttsSegments,
    wholeMessageTtsTexts,
    ttsContent,
  }
}

// 将 markdown 渲染成 Html，其中对话文本高亮显示（高亮需要配合 css）
export function transformToMarkdownHtml(text: string): string {
  // 检查是否是 cinema 模式消息，如果是则先转换为 Markdown
  let processableText = text
  if (isCinemaMessage(text)) {
    processableText = convertCinemaXmlToMarkdown(text)
  }

  const cleanText = DOMPurify.sanitize(processableText, { ALLOWED_TAGS: [] })
  // 先确保引号前的换行处理正确，防止 <br> 被块级元素吸收
  const processedText = ensureQuoteParagraphSeparation(cleanText)
  const dirtyHtml = marked(processedText) as string
  const cleanedHtml = dirtyHtml.trimEnd()
  const finalBeforeSanitize = cleanedHtml.replace(/&#8203;/g, '')
  return DOMPurify.sanitize(finalBeforeSanitize)
}

// 将 markdown 渲染成 Html，不保留标签
export function transformToMarkdownHtmlPlainText(text: string): string {
  // 检查是否是 cinema 模式消息，如果是则先转换为 Markdown
  let processableText = text
  if (isCinemaMessage(text)) {
    processableText = convertCinemaXmlToMarkdown(text)
  }

  const cleanText = DOMPurify.sanitize(processableText, { ALLOWED_TAGS: [] })
  const dirtyHtml = marked(cleanText) as string
  return DOMPurify.sanitize(dirtyHtml, { ALLOWED_TAGS: [] })
}

// 将 markdown 渲染成 Html，不保留image、code标签
export function transformToMarkdownHtmlWithoutImageAndCode(
  text: string
): string {
  // 检查是否是 cinema 模式消息，如果是则先转换为 Markdown
  let processableText = text
  if (isCinemaMessage(text)) {
    processableText = convertCinemaXmlToMarkdown(text)
  }

  const cleanText = DOMPurify.sanitize(processableText, { ALLOWED_TAGS: [] })
  // 先确保引号前的换行处理正确，防止 <br> 被块级元素吸收
  const processedText = ensureQuoteParagraphSeparation(cleanText)
  const dirtyHtml = marked(processedText) as string
  const cleanedHtml = dirtyHtml.trimEnd()
  const finalBeforeSanitize = cleanedHtml.replace(/&#8203;/g, '')
  return DOMPurify.sanitize(finalBeforeSanitize, {
    FORBID_TAGS: ['img', 'code'],
  })
}
